import { TaskTemplateData } from '@models/taskTemplate.model';
import { PlanChange } from '@models/projectPlanDraft.model';

/**
 * Sprint 31, story-161. Pure diff-to-animation-state functions driving `TaskTree`'s animated
 * review, shared in spirit with `redesign/src/utils/projectPlanDraft.utils.ts` (mobile's own
 * copy — no cross-package import between `web` and `redesign` exists in this codebase, so the
 * logic is duplicated, not shared, the same way `plan-diff.util.ts`'s backend logic has no
 * frontend counterpart to import from).
 */

export interface PlanRow {
  tempId: string;
  depth: number;
  task: TaskTemplateData;
}

/** Two-level flatten (phase -> subtask), matching `TemplateDetailModal`'s existing tree shape. */
export function flattenPlanToRows(tasks: TaskTemplateData[]): PlanRow[] {
  const byParent = new Map<string, TaskTemplateData[]>();
  const roots: TaskTemplateData[] = [];
  for (const t of tasks) {
    if (t.parentTempId) {
      const arr = byParent.get(t.parentTempId) ?? [];
      arr.push(t);
      byParent.set(t.parentTempId, arr);
    } else {
      roots.push(t);
    }
  }
  const rows: PlanRow[] = [];
  for (const root of roots) {
    rows.push({ tempId: root.tempId, depth: 0, task: root });
    for (const child of byParent.get(root.tempId) ?? []) {
      rows.push({ tempId: child.tempId, depth: 1, task: child });
    }
  }
  return rows;
}

export type PlanRowAnimState = 'added' | 'updated' | 'removed' | 'none';

/** `PlanChange.op` is `'ADDED' | 'REMOVED' | 'UPDATED'` (the backend/GraphQL contract's own
 * casing) — `PlanRowAnimState` is lowercase (what `TaskTree` actually checks against for its
 * ring/highlight classes). Sprint 31 review fix: `applyPlanChanges` used to cast `change.op`
 * directly into `animState` with no case conversion, so `row.animState === 'added'`/`'updated'`
 * never matched a real diff's `'ADDED'`/`'UPDATED'` — the highlight animation silently never
 * fired on any turn driven by an actual backend diff. */
function toAnimState(op: PlanChange['op'] | undefined): PlanRowAnimState {
  if (op === 'ADDED') return 'added';
  if (op === 'UPDATED') return 'updated';
  if (op === 'REMOVED') return 'removed';
  return 'none';
}

export interface AnimatedPlanRow extends PlanRow {
  animState: PlanRowAnimState;
  changedFields: Set<string>;
  /** Stagger delay in ms for entering rows — `index * 50ms`, per the UX spec's animation sequence. */
  delayMs: number;
}

/** Empty-diff fallback (UX spec, "Empty diff fallback"): a structural diff by tempId. */
function structuralDiff(prevTasks: TaskTemplateData[], nextTasks: TaskTemplateData[]): PlanChange[] {
  const prevById = new Map(prevTasks.map((t) => [t.tempId, t]));
  const nextById = new Map(nextTasks.map((t) => [t.tempId, t]));
  const changes: PlanChange[] = [];
  for (const t of nextTasks) {
    if (!prevById.has(t.tempId)) {
      changes.push({ op: 'ADDED', tempId: t.tempId, fields: [] });
    } else if (JSON.stringify(prevById.get(t.tempId)) !== JSON.stringify(t)) {
      changes.push({ op: 'UPDATED', tempId: t.tempId, fields: [{ field: 'unknown', from: null, to: null }] });
    }
  }
  for (const t of prevTasks) {
    if (!nextById.has(t.tempId)) {
      changes.push({ op: 'REMOVED', tempId: t.tempId, fields: [] });
    }
  }
  return changes;
}

/**
 * Computes the added/updated row list `TaskTree` renders. Sprint 36, story-187: rows for tasks
 * removed by the latest turn are never rendered (no strikethrough, no lingering previous-plan
 * rows). Removals feed only the change summary (`removedTaskNames`).
 */
export function applyPlanChanges(
  prevTasks: TaskTemplateData[],
  nextTasks: TaskTemplateData[],
  changes: PlanChange[],
): AnimatedPlanRow[] {
  const effectiveChanges = changes.length ? changes : structuralDiff(prevTasks, nextTasks);
  const effByTempId = new Map(effectiveChanges.map((c) => [c.tempId, c]));

  let addedIndex = 0;
  return flattenPlanToRows(nextTasks).map((r) => {
    const change = effByTempId.get(r.tempId);
    const animState: PlanRowAnimState = change?.op === 'REMOVED' ? 'none' : toAnimState(change?.op);
    const delayMs = animState === 'added' ? addedIndex++ * 50 : 0;
    return {
      ...r,
      animState,
      changedFields: new Set((change?.fields ?? []).map((f) => f.field)),
      delayMs,
    };
  });
}

/**
 * Walkthrough fix (sprint 38). The changes worth highlighting: only a REVISION's diff. The initial
 * turn's `lastChanges` is a diff against an empty plan (every task ADDED), and it persists across
 * reloads, so highlighting it outlined every card permanently. `turnCount` counts completed turns:
 * 1 after the initial plan, 2+ once a revision has landed.
 */
export function revisionChanges(draft: { turnCount?: number | null; lastChanges?: PlanChange[] | null }): PlanChange[] {
  return (draft.turnCount ?? 0) > 1 ? (draft.lastChanges ?? []) : [];
}

/** Names of tasks the latest turn removed, looked up from a tempId -> name cache of earlier plans. */
export function removedTaskNames(changes: PlanChange[], nameByTempId: Map<string, string>): string[] {
  return changes
    .filter((c) => c.op === 'REMOVED')
    .map((c) => nameByTempId.get(c.tempId))
    .filter((n): n is string => !!n);
}

/** Counts a phase's subtasks (for the delete confirmation) and removes a task plus its subtree. */
export function countSubtasks(tasks: TaskTemplateData[], tempId: string): number {
  return tasks.filter((t) => t.parentTempId === tempId).length;
}

export function removeTaskWithSubtree(tasks: TaskTemplateData[], tempId: string): TaskTemplateData[] {
  const gone = new Set<string>([tempId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const t of tasks) {
      if (t.parentTempId && gone.has(t.parentTempId) && !gone.has(t.tempId)) {
        gone.add(t.tempId);
        grew = true;
      }
    }
  }
  return tasks
    .filter((t) => !gone.has(t.tempId))
    .map((t) =>
      t.presedentTempIds?.some((id) => gone.has(id))
        ? { ...t, presedentTempIds: t.presedentTempIds.filter((id) => !gone.has(id)) }
        : t,
    );
}

/** Static (non-animated) rows — for read-only/template-display contexts. */
export function toStaticRows(tasks: TaskTemplateData[]): AnimatedPlanRow[] {
  return flattenPlanToRows(tasks).map((r) => ({ ...r, animState: 'none' as const, changedFields: new Set<string>(), delayMs: 0 }));
}

/** Aggregate totals across a flat task list — reused by `PlanReview`'s summary header. */
export function planTotals(tasks: TaskTemplateData[]) {
  let manHours = 0;
  let budget = 0;
  let materials = 0;
  let equipment = 0;
  for (const t of tasks) {
    manHours += t.manHours || 0;
    budget += t.budget || 0;
    materials += t.warehouseMaterials?.length || 0;
    equipment += t.warehouseEquipments?.length || 0;
  }
  return { taskCount: tasks.length, manHours, budget, materials, equipment };
}
